import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { AGENT_TOUR_START_EVENT } from '../ai/context/AIAgentContext';
import type {
  TourDefinition,
  TourStep,
  TourId,
  UserTourProgress,
  UserOnboarding,
} from '../types/onboarding';
import { TOUR_ROUTES, FULL_TOUR_SEQUENCE } from '../types/onboarding';
import {
  getTourDefinitions,
  getTourById,
  getUserOnboarding,
  getAllUserProgress,
  startTour as startTourService,
  advanceStep,
  completeTour,
  skipTour as skipTourService,
  resetTour as resetTourService,
  resetAllTours as resetAllToursService,
  markWelcomeSeen,
  markFullTourCompleted,
  isTourCompleted,
} from '../services/onboardingService';
import { logger } from '../lib/logger';

interface OnboardingContextType {
  // State
  isActive: boolean;
  currentTour: TourDefinition | null;
  currentStep: TourStep | null;
  currentStepIndex: number;
  totalSteps: number;
  progress: UserTourProgress[];
  onboarding: UserOnboarding | null;
  tours: TourDefinition[];
  isLoading: boolean;

  // Actions
  startTour: (tourId: TourId) => Promise<void>;
  startFullTour: () => Promise<void>;
  nextStep: () => Promise<void>;
  prevStep: () => void;
  skipTour: () => Promise<void>;
  endTour: () => void;
  resetTour: (tourId: TourId) => Promise<void>;
  resetAllTours: () => Promise<void>;

  // Queries
  isTourDone: (tourId: TourId) => boolean;
  getTourProgress: (tourId: TourId) => number;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

interface OnboardingProviderProps {
  children: ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Tour state
  const [isActive, setIsActive] = useState(false);
  const [currentTour, setCurrentTour] = useState<TourDefinition | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isFullTour, setIsFullTour] = useState(false);
  const [fullTourIndex, setFullTourIndex] = useState(0);

  // Data state
  const [tours, setTours] = useState<TourDefinition[]>([]);
  const [progress, setProgress] = useState<UserTourProgress[]>([]);
  const [onboarding, setOnboarding] = useState<UserOnboarding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasCheckedWelcome, setHasCheckedWelcome] = useState(false);

  // Refs for stable callbacks
  const startTourRef = useRef<(tourId: TourId) => Promise<void>>();
  const startFullTourRef = useRef<() => Promise<void>>();

  // Computed values
  const currentStep = currentTour?.steps[currentStepIndex] ?? null;
  const totalSteps = currentTour?.steps.length ?? 0;

  // Load initial data
  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const [toursData, progressData, onboardingData] = await Promise.all([
          getTourDefinitions(),
          getAllUserProgress(user.id),
          getUserOnboarding(user.id),
        ]);

        setTours(toursData);
        setProgress(progressData);
        setOnboarding(onboardingData);
      } catch (error) {
        logger.error('Error loading onboarding data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [user?.id]);

  // Auto-start welcome tour for new users
  useEffect(() => {
    if (
      isLoading ||
      !user?.id ||
      hasCheckedWelcome ||
      isActive ||
      location.pathname !== '/dashboard'
    ) {
      return;
    }

    setHasCheckedWelcome(true);

    // Check if user has seen welcome
    if (!onboarding?.has_seen_welcome) {
      // Small delay to let the page render first
      const timer = setTimeout(() => {
        startFullTourInternal();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isLoading, user?.id, onboarding, hasCheckedWelcome, isActive, location.pathname]);

  // Start a single tour
  const startTour = useCallback(
    async (tourId: TourId) => {
      if (!user?.id) return;

      try {
        const tour = await getTourById(tourId);
        if (!tour) {
          logger.error('Tour not found:', tourId);
          return;
        }

        // Navigate to the tour's starting page if needed
        const targetRoute = TOUR_ROUTES[tourId];
        if (location.pathname !== targetRoute) {
          navigate(targetRoute);
          // Wait for navigation
          await new Promise((resolve) => setTimeout(resolve, 300));
        }

        await startTourService(user.id, tourId);
        setCurrentTour(tour);
        setCurrentStepIndex(0);
        setIsActive(true);
        setIsFullTour(false);

        // Refresh progress
        const newProgress = await getAllUserProgress(user.id);
        setProgress(newProgress);
      } catch (error) {
        logger.error('Error starting tour:', error);
      }
    },
    [user?.id, location.pathname, navigate]
  );

  // Keep refs updated
  startTourRef.current = startTour;

  // Internal function for auto-starting full tour
  const startFullTourInternal = useCallback(async () => {
    if (!user?.id) return;

    try {
      // Mark welcome as seen
      await markWelcomeSeen(user.id);

      const firstTourId = FULL_TOUR_SEQUENCE[0];
      const tour = await getTourById(firstTourId);
      if (!tour) return;

      // Navigate if needed
      const targetRoute = TOUR_ROUTES[firstTourId];
      if (location.pathname !== targetRoute) {
        navigate(targetRoute);
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      await startTourService(user.id, firstTourId);
      setCurrentTour(tour);
      setCurrentStepIndex(0);
      setIsActive(true);
      setIsFullTour(true);
      setFullTourIndex(0);

      // Refresh data
      const [newProgress, newOnboarding] = await Promise.all([
        getAllUserProgress(user.id),
        getUserOnboarding(user.id),
      ]);
      setProgress(newProgress);
      setOnboarding(newOnboarding);
    } catch (error) {
      logger.error('Error starting full tour:', error);
    }
  }, [user?.id, location.pathname, navigate]);

  // Start full tour (user-triggered)
  const startFullTour = useCallback(async () => {
    await startFullTourInternal();
  }, [startFullTourInternal]);

  // Keep refs updated
  startFullTourRef.current = startFullTour;

  // Listen for AI agent tour start events
  useEffect(() => {
    const handleTourStart = (event: CustomEvent<{ tourId: string }>) => {
      const { tourId } = event.detail;

      if (tourId === 'full') {
        startFullTourRef.current?.();
      } else {
        startTourRef.current?.(tourId as TourId);
      }
    };

    window.addEventListener(AGENT_TOUR_START_EVENT, handleTourStart as EventListener);
    return () => {
      window.removeEventListener(AGENT_TOUR_START_EVENT, handleTourStart as EventListener);
    };
  }, []);

  // Next step
  const nextStep = useCallback(async () => {
    if (!user?.id || !currentTour) return;

    const isLastStep = currentStepIndex >= totalSteps - 1;

    if (isLastStep) {
      // Complete current tour
      await completeTour(user.id, currentTour.id as TourId);

      // Check if we should continue to next tour in full tour
      if (isFullTour && fullTourIndex < FULL_TOUR_SEQUENCE.length - 1) {
        const nextTourId = FULL_TOUR_SEQUENCE[fullTourIndex + 1];
        const nextTour = await getTourById(nextTourId);

        if (nextTour) {
          // Navigate to next tour's page
          const targetRoute = TOUR_ROUTES[nextTourId];
          if (location.pathname !== targetRoute) {
            navigate(targetRoute);
            await new Promise((resolve) => setTimeout(resolve, 300));
          }

          await startTourService(user.id, nextTourId);
          setCurrentTour(nextTour);
          setCurrentStepIndex(0);
          setFullTourIndex(fullTourIndex + 1);

          const newProgress = await getAllUserProgress(user.id);
          setProgress(newProgress);
          return;
        }
      }

      // Full tour complete or single tour done
      if (isFullTour) {
        await markFullTourCompleted(user.id);
        const newOnboarding = await getUserOnboarding(user.id);
        setOnboarding(newOnboarding);
      }

      // End tour
      setIsActive(false);
      setCurrentTour(null);
      setCurrentStepIndex(0);
      setIsFullTour(false);
      setFullTourIndex(0);

      const newProgress = await getAllUserProgress(user.id);
      setProgress(newProgress);
    } else {
      // Advance to next step
      await advanceStep(user.id, currentTour.id as TourId);
      setCurrentStepIndex(currentStepIndex + 1);

      const newProgress = await getAllUserProgress(user.id);
      setProgress(newProgress);
    }
  }, [user?.id, currentTour, currentStepIndex, totalSteps, isFullTour, fullTourIndex, location.pathname, navigate]);

  // Previous step
  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  }, [currentStepIndex]);

  // Skip tour
  const skipTour = useCallback(async () => {
    if (!user?.id || !currentTour) return;

    try {
      await skipTourService(user.id, currentTour.id as TourId);

      // If full tour, mark all as skipped
      if (isFullTour) {
        for (const tourId of FULL_TOUR_SEQUENCE) {
          await skipTourService(user.id, tourId);
        }
        await markFullTourCompleted(user.id);
        const newOnboarding = await getUserOnboarding(user.id);
        setOnboarding(newOnboarding);
      }

      setIsActive(false);
      setCurrentTour(null);
      setCurrentStepIndex(0);
      setIsFullTour(false);
      setFullTourIndex(0);

      const newProgress = await getAllUserProgress(user.id);
      setProgress(newProgress);
    } catch (error) {
      logger.error('Error skipping tour:', error);
    }
  }, [user?.id, currentTour, isFullTour]);

  // End tour without saving
  const endTour = useCallback(() => {
    setIsActive(false);
    setCurrentTour(null);
    setCurrentStepIndex(0);
    setIsFullTour(false);
    setFullTourIndex(0);
  }, []);

  // Reset a single tour
  const resetTour = useCallback(
    async (tourId: TourId) => {
      if (!user?.id) return;

      try {
        await resetTourService(user.id, tourId);
        const newProgress = await getAllUserProgress(user.id);
        setProgress(newProgress);
      } catch (error) {
        logger.error('Error resetting tour:', error);
      }
    },
    [user?.id]
  );

  // Reset all tours
  const resetAllTours = useCallback(async () => {
    if (!user?.id) return;

    try {
      await resetAllToursService(user.id);
      const [newProgress, newOnboarding] = await Promise.all([
        getAllUserProgress(user.id),
        getUserOnboarding(user.id),
      ]);
      setProgress(newProgress);
      setOnboarding(newOnboarding);
    } catch (error) {
      logger.error('Error resetting all tours:', error);
    }
  }, [user?.id]);

  // Query functions
  const isTourDone = useCallback(
    (tourId: TourId) => isTourCompleted(progress, tourId),
    [progress]
  );

  const getTourProgressPercent = useCallback(
    (tourId: TourId) => {
      const tour = tours.find((t) => t.id === tourId);
      if (!tour) return 0;

      const tourProgress = progress.find((p) => p.tour_id === tourId);
      if (!tourProgress) return 0;
      if (tourProgress.completed) return 100;

      return Math.round((tourProgress.current_step / tour.steps.length) * 100);
    },
    [progress, tours]
  );

  return (
    <OnboardingContext.Provider
      value={{
        isActive,
        currentTour,
        currentStep,
        currentStepIndex,
        totalSteps,
        progress,
        onboarding,
        tours,
        isLoading,
        startTour,
        startFullTour,
        nextStep,
        prevStep,
        skipTour,
        endTour,
        resetTour,
        resetAllTours,
        isTourDone,
        getTourProgress: getTourProgressPercent,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}
